import styled from 'styled-components';
import Chips from '../Chips';
import ImageWrapper from '../../../../new-components/ImageWrapper';
import HeroDetails from '../HeroDetails';
import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { useRouter } from 'next/router';

const HeroSection = ({
  animate,
  propertiesRef,
  developerLogos
}: {
  animate: boolean;
  propertiesRef: React.RefObject<HTMLDivElement>;
  developerLogos: Array<{ logo: string }>;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const slidingTextRef = useRef<HTMLDivElement>(null);
  const homeImageRef = useRef<HTMLDivElement>(null);
  const [isFluid, setIsFluid] = useState(true);
  const backgroundRef = useRef<HTMLDivElement>(null);
  const { locale } = useRouter();

  useEffect(() => {
    if (backgroundRef.current) {
      gsap.fromTo(
        backgroundRef.current,
        { scale: 0, transformOrigin: 'center top' },
        { scale: 1, duration: 1.2, ease: 'back.out(3)' }
      );
    }
  }, []);

  useEffect(() => {
    if (animate) {
      setIsFluid(false);
      if (containerRef.current) {
        gsap.to(containerRef.current, {
          height: '165px',
          duration: 1,
          marginTop: 40,
          ease: 'power2.inOut'
        });
      }
      if (slidingTextRef.current) {
        gsap.to(slidingTextRef.current, {
          opacity: 0,
          x: locale === 'ar' ? -100 : 100,
          duration: 1,
          ease: 'power2.inOut'
        });
      }
      if (homeImageRef.current) {
        gsap.to(homeImageRef.current, {
          duration: 1,
          ease: 'power2.inOut',
          right: locale === 'ar' ? 'unset' : '-30px',
          left: locale === 'ar' ? '-30px' : 'unset'
        });
      }
    } else {
      setIsFluid(true);
      if (containerRef.current) {
        gsap.to(containerRef.current, {
          height: '125px',
          marginTop: 140,
          duration: 1,
          ease: 'power2.inOut'
        });
      }
      if (slidingTextRef.current) {
        gsap.to(slidingTextRef.current, {
          opacity: 1,
          x: 0,
          duration: 1,
          ease: 'power2.inOut'
        });
      }
      if (homeImageRef.current) {
        gsap.to(homeImageRef.current, {
          duration: 1,
          ease: 'power2.inOut',
          right: locale === 'ar' ? 'unset' : '0',
          left: locale === 'ar' ? '0' : 'unset'
        });
      }
    }
  }, [animate, locale]);

  useEffect(() => {
    if (slidingTextRef.current) {
      gsap.fromTo(
        slidingTextRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 1.2, ease: 'back.out(3)' }
      );
    }
    if (homeImageRef.current) {
      gsap.fromTo(
        homeImageRef.current,
        { x: locale === 'ar' ? -100 : 100 },
        { x: 0, duration: 1.2, ease: 'back.out(3)' }
      );
    }
  }, [locale]);

  return (
    <Container
      ref={containerRef}
      className={isFluid ? 'container-fluid' : ''}
      locale={locale}
    >
      <div className="background" ref={backgroundRef} />
      <Chips animate={animate} developerLogos={developerLogos} />
      <div className="sliding-text" ref={slidingTextRef}>
        <ImageWrapper
          src={
            locale == 'ar'
              ? '/assets/images/nawy-now/nawy-now-typo-ar.png'
              : '/assets/images/nawy-now/nawy-now-typo.png'
          }
          alt="Nawy Now Hero Text"
          className="hero-text"
          fill
        />
      </div>
      <div className="home-image" ref={homeImageRef}>
        <ImageWrapper
          src={
            locale == 'en'
              ? '/assets/images/nawy-now/mobile-home.png'
              : '/assets/images/nawy-now/mobile-home-ar.png'
          }
          alt="Nawy Now Home"
          className="hero-image"
          fill
          style={{ objectFit: 'cover', objectPosition: 'center' }}
        />
      </div>

      <HeroDetails animate={animate} propertiesRef={propertiesRef} />
    </Container>
  );
};

const Container = styled.div<{ locale?: string }>`
  position: relative;
  height: 125px;
  margin: auto;
  margin-top: 140px;
  width: 100vw;
  .background {
    position: absolute;
    inset: 0;
    z-index: 0;
    background: ${props =>
      props.locale == 'en'
        ? 'linear-gradient(299deg, #FFD6BF 1.99%, #FFF0E8 71.08%)'
        : 'linear-gradient(120deg, #FFD6BF 1.99%, #FFF0E8 71.08%)'};
    width: 100%;
    height: 100%;
    will-change: transform;
  }
  /* overflow-x: hidden; */
  .sliding-text {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: ${props => (props?.locale === 'ar' ? 'unset' : '4%')};
    right: ${props => (props?.locale === 'ar' ? '4%' : 'unset')};
    width: ${props => (props?.locale === 'ar' ? '128px' : '235px')};
    height: 100px;
    display: flex;
    justify-content: center;
    align-items: center;
    filter: brightness(0) saturate(100%) invert(46%) sepia(94%) saturate(7492%)
      hue-rotate(2deg) brightness(101%) contrast(105%);

    .hero-text {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    }
  }

  .home-image {
    position: absolute;
    bottom: 0;
    right: ${props => (props?.locale === 'ar' ? 'unset' : '0')};
    left: ${props => (props?.locale === 'ar' ? '0' : 'unset')};
    width: 250px;
    height: 180px;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
  }
`;

export default HeroSection;
